import { Injectable } from '@angular/core';
import {
	ColumnModel,
	listToDictionaryObject,
	fileBooleanToString,
	formatDateTimeFormToDb,
	formatDateFormToDb,
	PrimaryKeyValueModel,
	mapPrimaryKeysArrayToObject,
	DateTimeService
} from '@shared';
import { FormControlTypeEnum } from '../models';
import { ColumnRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { ColumnDataTypes } from '@database/models';
import { FormControlTypeProviderService } from '../factories';
import { Observable, map } from 'rxjs';
import { ColumnInformationService } from '../validations/services/column-information.service';

@Injectable()
export class NormalizeFormValuesForDatabaseService {
	public constructor(
		private formControlTypeProviderService: FormControlTypeProviderService,
		private columnRepository: ColumnRepository,
		private dateTimeService: DateTimeService,
		private columnInformationService: ColumnInformationService
	) {}

	public prepareValuesBasedOnDataType(
		columns: ColumnModel[],
		formValues: { [key: string]: string | number | boolean }
	): { [key: string]: string | number | boolean } {
		const dataColumnsDictionary = listToDictionaryObject(WorkspaceDatabaseKeys.columns.columnName, columns);

		Object.entries(formValues).forEach(([fieldKey, fieldValue]) => {
			const column: ColumnModel = dataColumnsDictionary[fieldKey];
			const formFieldType = this.formControlTypeProviderService.getFormControlType(column);

			if (formFieldType === FormControlTypeEnum.CheckBox) {
				formValues[fieldKey] = fileBooleanToString(Boolean(fieldValue));
			}

			if (column.data_type === ColumnDataTypes.NUMERIC) {
				if (
					column.column_name === WorkspaceDatabaseKeys.columns.depthFrom ||
					column.column_name === WorkspaceDatabaseKeys.columns.depthTo
				) {
					formValues[fieldKey] = this.hasValue(fieldValue) ? Number(fieldValue) : undefined;
				} else {
					formValues[fieldKey] = this.hasValue(fieldValue) ? fieldValue.toString() : undefined;
				}
			}

			// ToDo: this is an attempt to keep consistency in rxdb data and during the export routine;
			// if a field is left empty in form,do not save it;
			if (column.data_type === ColumnDataTypes.CHAR && formFieldType !== FormControlTypeEnum.CheckBox) {
				formValues[fieldKey] = fieldValue !== '' ? fieldValue : undefined;
			}

			if (column.data_type === ColumnDataTypes.DATETIME) {
				if (this.columnInformationService.isColumnTypeTime(column)) {
					formValues[fieldKey] = fieldValue
						? this.dateTimeService.toFusionTimeFieldsFormat(String(fieldValue))
						: undefined;
				} else {
					formValues[fieldKey] = fieldValue ? formatDateTimeFormToDb(String(fieldValue)) : undefined;
				}
			}

			if (column.data_type === ColumnDataTypes.DATE) {
				formValues[fieldKey] = fieldValue ? formatDateFormToDb(String(fieldValue)) : undefined;
			}
		});

		return formValues;
	}

	public preparePrimaryKeysValues$(
		tableName: string,
		primaryKeyValue: PrimaryKeyValueModel[]
	): Observable<PrimaryKeyValueModel[]> {
		return this.columnRepository.getColumns$(tableName).pipe(
			map((columns: ColumnModel[]) => {
				const primaryKeysAndValues = this.prepareValuesBasedOnDataType(
					columns,
					mapPrimaryKeysArrayToObject(primaryKeyValue)
				);
				return Object.keys(primaryKeysAndValues).reduce((acc, currPrimaryKey) => {
					acc.push({ primaryKey: currPrimaryKey, primaryKeyValue: primaryKeysAndValues[currPrimaryKey] });
					return acc;
				}, []);
			})
		);
	}

	private hasValue(fieldValue: string | number | boolean): boolean {
		return (fieldValue && fieldValue !== '') || fieldValue === 0;
	}
}
